import { Component, effect, HostBinding, OnInit, signal } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { AuthenticationService, LocalStorageService, ToastService, UsersService } from '../_services';
import { Router } from '@angular/router';
import { Role } from '../_enums/roles';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatRippleModule } from '@angular/material/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { lastValueFrom, Subject } from 'rxjs';
import { ScreenBannerComponent } from './screen-banner/screen-banner.component';
import { RouterLink } from '@angular/router';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { environment } from '../../environments/environment';
import { HttpErrorResponse } from '@angular/common/http';
import { TeamsAuthService } from '../_services/teams-auth.service';
import { MSEntraProofs, startWebAuth, storePendingExchange } from './ms-entra/ms-entra-state';

const MS_ENTRA_LOGIN_LOG_PREFIX = '[MSEntraLogin]';

@Component({
  selector: 'app-authentication',
  templateUrl: './authentication.component.html',
  styleUrls: ['./authentication.component.scss'],
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatRippleModule,
    MatCheckboxModule,
    ReactiveFormsModule,
    ScreenBannerComponent,
    RouterLink,
    MatProgressSpinnerModule,
  ],
})
export class AuthenticationComponent implements OnInit {
  @HostBinding('class') class = 'flex flex-col flex-grow';
  passwordVisible = false;
  isLoading = signal(false);
  isLoadingResults = false;
  showContent = signal(false);
  isRunningInTeams = signal(false);
  isMicrosoftLoading = signal(false);
  rateLimitMessage = signal<string | null>(null);
  private rateLimitTimer: ReturnType<typeof setTimeout> | null = null;

  errorMessages = {
    required: 'This field is required',
    email: 'Please provide a valid email address',
    badEmailOrPassword: 'Email or password is incorrect',
  };

  protected onDestroy = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private authSvc: AuthenticationService,
    private toastService: ToastService,
    private localStorage: LocalStorageService,
    private userService: UsersService,
    private teamsAuthService: TeamsAuthService
  ) {
    effect(() => {
      const user = this.authSvc.userSignal();
      if (user) {
        if (this.authSvc.redirectUrl) {
          this.router.navigate([this.authSvc.redirectUrl]);
          this.authSvc.redirectUrl = null;
          return;
        } else if ([Role.CONTROL].includes(user.role)) {
          this.router.navigate(['/admin']);
          return;
        } else if (user.role === Role.SUPPORT) {
          this.router.navigate(['/slicks/my']);
          return;
        }
        this.router.navigate(['/slicks/feed']);
        return;
      }
      this.showContent.set(true);
    });
  }

  async ngOnInit(): Promise<void> {
    console.info(`${MS_ENTRA_LOGIN_LOG_PREFIX} Authentication page initialized`);
    this.isRunningInTeams.set(await this.teamsAuthService.isRunningInTeams());
    console.info(`${MS_ENTRA_LOGIN_LOG_PREFIX} Initial Teams detection result`, {
      isRunningInTeams: this.isRunningInTeams(),
    });
  }

  loginForm: FormGroup = this.fb.group({
    email: new FormControl<string | null>('', [Validators.required, Validators.email]),
    password: new FormControl<string | null>('', [Validators.required, Validators.minLength(1)]),
  });

  resetForm: FormGroup = this.fb.group({
    email: new FormControl<string | null>('', [Validators.required, Validators.email]),
  });

  async login(loginForm): Promise<void> {
    if (!this.loginForm.valid) {
      return;
    }
    this.isLoading.set(true);
    try {
      await lastValueFrom(this.authSvc.login(loginForm.email, loginForm.password));

      this.isLoadingResults = true;
      this.localStorage.remove('topics');
      this.localStorage.remove('slicks');
      this.localStorage.remove('slicksCount');
      this.localStorage.remove('favoritesCount');

      if (this.authSvc.accessToken != null && this.authSvc.accessToken != '') {
        const loginResult = await lastValueFrom(this.userService.getUserLoginInfo());
        this.authSvc.setUser(loginResult);
        this.toastService.queue.next({
          message: 'Welcome!',
          duration: 1000,
          panelClass: 'slick-custom-snackbar-success',
        });
      }
    } catch (e) {
      if (e instanceof HttpErrorResponse) {
        if (e.status === 429) {
          const message = e.error?.message || 'Too many attempts. Please try again later.';
          const retryAfterMs = (e.error?.retryAfter ?? 300) * 1000;
          this.rateLimitMessage.set(message);
          clearTimeout(this.rateLimitTimer);
          this.rateLimitTimer = setTimeout(() => this.rateLimitMessage.set(null), retryAfterMs);
        } else if (e.status === 401) {
          this.rateLimitMessage.set(null);
          clearTimeout(this.rateLimitTimer);
          this.loginForm.get('email').setErrors({ badEmailOrPassword: true });
          this.loginForm.get('password').setErrors({ badEmailOrPassword: true });
        }
      }
    } finally {
      this.isLoading.set(false);
    }
  }

  errors(ctrl: any): string[] {
    return ctrl.errors ? Object.keys(ctrl.errors) : [];
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  clearErrors(_: KeyboardEvent): void {
    this.rateLimitMessage.set(null);
    clearTimeout(this.rateLimitTimer);
    const emailControl = this.loginForm.get('email');
    const passwordControl = this.loginForm.get('password');

    if (emailControl) {
      emailControl.setErrors(null);
    }
    if (passwordControl) {
      passwordControl.setErrors(null);
    }
  }

  async continueWithMicrosoft(): Promise<void> {
    console.info(`${MS_ENTRA_LOGIN_LOG_PREFIX} Continue with Microsoft clicked`);

    if (this.isMicrosoftLoading()) {
      console.info(`${MS_ENTRA_LOGIN_LOG_PREFIX} Ignoring click while Microsoft login is already loading`);
      return;
    }

    this.isMicrosoftLoading.set(true);

    try {
      const isRunningInTeams = this.isRunningInTeams() || (await this.teamsAuthService.isRunningInTeams());
      this.isRunningInTeams.set(isRunningInTeams);
      const isEmbeddedInFrame = this.teamsAuthService.isEmbeddedInFrame();
      const shouldUseTeamsPopup = isRunningInTeams || isEmbeddedInFrame;

      console.info(`${MS_ENTRA_LOGIN_LOG_PREFIX} Microsoft login branch selected`, {
        isRunningInTeams,
        isEmbeddedInFrame,
        shouldUseTeamsPopup,
      });

      if (!shouldUseTeamsPopup) {
        console.info(`${MS_ENTRA_LOGIN_LOG_PREFIX} Redirecting browser to Entra authorize URL`);
        const msEntraURL = await this.createMSEntraURL();
        if (!msEntraURL) {
          throw new Error('Microsoft login is not configured.');
        }
        window.location.assign(msEntraURL);
        return;
      }

      const popupUrl = new URL('teams-ms-entra-start', environment.baseUrl);
      console.info(`${MS_ENTRA_LOGIN_LOG_PREFIX} Opening Teams authentication popup`, {
        popupUrl: popupUrl.toString(),
      });

      const authenticationResult = await this.teamsAuthService.authenticate(popupUrl.toString());
      console.info(`${MS_ENTRA_LOGIN_LOG_PREFIX} Teams authentication popup returned result`);
      const { code, code_verifier, nonce } = JSON.parse(authenticationResult) as {
        code?: string;
      } & Partial<MSEntraProofs>;

      if (!code) {
        throw new Error('Microsoft authentication did not return an authorization code.');
      }
      if (!code_verifier || !nonce) {
        throw new Error('Microsoft authentication could not be verified. Please try again.');
      }
      // The /ms-entra callback only exchanges a code this tab has proofs for.
      storePendingExchange({ code_verifier, nonce });

      console.info(`${MS_ENTRA_LOGIN_LOG_PREFIX} Navigating to MS Entra callback for backend exchange`);
      await this.router.navigate(['/ms-entra'], { queryParams: { code } });
    } catch (error) {
      console.error('Microsoft Teams authentication failed:', error);
      const message = error instanceof Error ? error.message : 'Failed to login with Microsoft!';

      this.toastService.queue.next({
        message,
        duration: 1500,
        panelClass: 'slick-custom-snackbar-error',
      });
    } finally {
      this.isMicrosoftLoading.set(false);
    }
  }

  async createMSEntraURL(): Promise<string> {
    const authorize = new URL(environment.authority);

    if (!environment.redirectUri || !environment.clientId) {
      console.error('Missing environment variables for Microsoft Entra URL');
      return '';
    }

    const { state, code_challenge, nonce } = await startWebAuth();

    authorize.searchParams.append('redirect_uri', environment.redirectUri);
    authorize.searchParams.append('scope', 'user.read openid profile offline_access');
    authorize.searchParams.append('client_id', environment.clientId);
    authorize.searchParams.append('response_type', 'code');
    authorize.searchParams.append('response_mode', 'query');
    authorize.searchParams.append('prompt', 'select_account');
    authorize.searchParams.append('state', state);
    authorize.searchParams.append('nonce', nonce);
    authorize.searchParams.append('code_challenge', code_challenge);
    authorize.searchParams.append('code_challenge_method', 'S256');

    return authorize.toString();
  }

  ngOnDestroy(): void {
    clearTimeout(this.rateLimitTimer);
    this.onDestroy.next();
    this.onDestroy.complete();
  }
}
